import { AppNotificationType } from 'shared/entities/appNotifications';
import { apiUrls } from 'shared/entities/domain';
import {
  mapScenarioFolderCreateErrorToMessage,
  ScenarioFolderError,
  ScenarioFolderDeleteAction,
  ScenarioFolderDeleteParams,
  ScenarioFolderServer
} from 'shared/entities/scenarioFolder';
import { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStageModel } from 'shared/models/loadingStage';
import NewListModel from 'shared/models/NewListModel';
import { ScenarioFolderModel } from 'shared/models/scenarioFolder';

export default class ScenarioFoldersStore {
  readonly rootStore: IRootStore;
  /**
   * список папок, которые отображаются на странице, фильтруются поиском
   */
  readonly folders: NewListModel<ScenarioFolderModel> =
    new NewListModel<ScenarioFolderModel>();
  /**
   * список папок, которые отображаются в селекторе, не фильтруются поиском, используется для определения наличия папок
   */
  readonly allFolders: NewListModel<ScenarioFolderModel> =
    new NewListModel<ScenarioFolderModel>();

  readonly creatingStage: LoadingStageModel = new LoadingStageModel();
  readonly editingStage: LoadingStageModel = new LoadingStageModel();
  readonly deletingStage: LoadingStageModel = new LoadingStageModel();

  constructor({ rootStore }: { rootStore: IRootStore }) {
    this.rootStore = rootStore;
  }

  async loadFolders(data?: { searchText?: string }): Promise<BaseResponse> {
    if (this.folders.loadingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.folders.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      scenario_folders: ScenarioFolderServer[];
    }>(apiUrls.SCENARIO_FOLDERS_LIST, {
      method: 'GET',
      data: {
        q: data?.searchText || undefined
      }
    });

    if (response.isError) {
      this.folders.loadingStage.error();
      return {
        isError: true
      };
    }

    const { keys, entities } = NewListModel.getListFromArray(
      response.data.scenario_folders,
      (rawItem) => {
        const folder = ScenarioFolderModel.fromJson(rawItem);
        return {
          entity: folder,
          key: folder.id
        };
      }
    );

    this.folders.addEntities({
      entities,
      keys,
      initial: true
    });

    if (!data?.searchText) {
      this.allFolders.addEntities({
        entities,
        keys,
        initial: true
      });
    }

    this.folders.loadingStage.success();
    return { isError: false };
  }

  async createFolder({
    name
  }: {
    name: string;
  }): Promise<BaseResponse<ScenarioFolderModel, ScenarioFolderError | null>> {
    if (this.creatingStage.isLoading) {
      return {
        isError: true,
        data: null
      };
    }

    this.creatingStage.loading();

    const response = await this.rootStore.networkStore.api<
      ScenarioFolderServer,
      ScenarioFolderError
    >(apiUrls.SCENARIO_FOLDERS_CREATE, {
      method: 'POST',
      data: {
        name
      },
      errorsMap: mapScenarioFolderCreateErrorToMessage,
      expectedErrorCodes: [ScenarioFolderError.scenarioFolderAlreadyExists]
    });

    if (response.isError) {
      this.creatingStage.error();
      return { isError: true, data: response.data?.code || null };
    }

    const newFolder = ScenarioFolderModel.fromJson(response.data);

    this.folders.addEntity({
      entity: newFolder,
      key: newFolder.id,
      start: true
    });

    this.allFolders.addEntity({
      entity: newFolder,
      key: newFolder.id,
      start: true
    });

    this.creatingStage.success();

    this.rootStore.appNotificationsStore.open({
      title: (t) =>
        t('ScenarioFoldersStore.notifications.creatingSuccess', {
          ns: 'stores',
          name: name
        }),
      type: AppNotificationType.success,
      highlightedParts: [newFolder.title]
    });

    return { isError: false, data: newFolder };
  }

  editFolder = async ({
    name,
    folderId
  }: {
    name: string;
    folderId: string;
  }): Promise<
    BaseResponse<ScenarioFolderModel, ScenarioFolderError | null>
  > => {
    if (this.editingStage.isLoading) {
      return {
        isError: true,
        data: null
      };
    }

    this.editingStage.loading();

    const response = await this.rootStore.networkStore.api<
      ScenarioFolderServer,
      ScenarioFolderError
    >(apiUrls.SCENARIO_FOLDERS_UPDATE, {
      method: 'POST',
      data: {
        name,
        _id: folderId
      },
      errorsMap: mapScenarioFolderCreateErrorToMessage,
      expectedErrorCodes: [ScenarioFolderError.scenarioFolderAlreadyExists]
    });

    if (response.isError) {
      this.editingStage.error();
      return { isError: true, data: response.data?.code || null };
    }

    const folder = this.folders.getEntity(folderId);

    if (!folder) {
      this.editingStage.error();
      return { isError: true, data: null };
    }

    folder.updateTitle(name);
    this.editingStage.success();

    this.rootStore.appNotificationsStore.open({
      title: (t) =>
        t('ScenarioFoldersStore.notifications.editingSuccess', {
          ns: 'stores',
          name: name
        }),
      type: AppNotificationType.success
    });

    return { isError: false, data: folder };
  };

  deleteFolder = async ({
    id,
    action,
    moveToFolderId
  }: {
    id: string;
    action: ScenarioFolderDeleteAction;
    moveToFolderId?: string | null;
  }): Promise<BaseResponse> => {
    if (this.deletingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.deletingStage.loading();
    const data: ScenarioFolderDeleteParams =
      action === ScenarioFolderDeleteAction.delete
        ? {
            _id: id,
            action: ScenarioFolderDeleteAction.delete
          }
        : {
            _id: id,
            action: ScenarioFolderDeleteAction.move,
            move_to_folder_id: moveToFolderId ?? null
          };

    const response = await this.rootStore.networkStore.api(
      apiUrls.SCENARIO_FOLDERS_DELETE,
      {
        method: 'POST',
        data
      }
    );

    if (response.isError) {
      this.deletingStage.error();
      return { isError: true };
    }

    this.folders.removeEntity(id);
    this.allFolders.removeEntity(id);

    this.deletingStage.success();

    this.rootStore.appNotificationsStore.open({
      title: (t) =>
        t('ScenarioFoldersStore.notifications.deletingSuccess', {
          ns: 'stores'
        }),
      type: AppNotificationType.success
    });

    return { isError: false };
  };

  reset(): void {
    this.folders.reset();
  }
}
